import jsPDF from "jspdf";
import api from "../api";

const safe = (value) => String(value ?? "").trim();
const cleanFileName = (value) =>
  safe(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const formatDateTime = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
};

export const downloadAIInsightsPdf = async ({ report, title, module }) => {
  if (!report) throw new Error("Gere os insights antes de baixar o PDF.");

  let company = null;
  try {
    company = (await api.get("/company")).data;
  } catch {
    company = null;
  }

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const margin = 46;
  const contentWidth = width - margin * 2;
  let y = 0;

  const ensureSpace = (needed = 50) => {
    if (y + needed <= height - 58) return;
    doc.addPage();
    y = 54;
  };

  const addWrapped = (text, options = {}) => {
    const value = safe(text);
    if (!value) return;
    const size = options.size || 10;
    const weight = options.bold ? "bold" : "normal";
    const indent = options.indent || 0;
    const gap = options.gap ?? 5;
    doc.setFont("helvetica", weight);
    doc.setFontSize(size);
    doc.setTextColor(options.muted ? 92 : 35, options.muted ? 105 : 48, options.muted ? 125 : 68);
    const lines = doc.splitTextToSize(value, contentWidth - indent);
    ensureSpace(lines.length * (size + 3) + gap);
    doc.text(lines, margin + indent, y);
    y += lines.length * (size + 3) + gap;
  };

  const sectionTitle = (label) => {
    ensureSpace(34);
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(7, 31, 84);
    doc.text(label, margin, y);
    y += 18;
  };

  const bulletList = (items = []) => {
    (Array.isArray(items) ? items : []).filter(Boolean).forEach((item) => {
      addWrapped("• " + safe(item), { indent: 4, gap: 4 });
    });
  };

  doc.setFillColor(7, 31, 84);
  doc.rect(0, 0, width, 116, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(title || "Relatório com Insights de IA", margin, 48);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const companyName = safe(company?.tradeName || company?.name || company?.corporateName);
  if (companyName) doc.text(companyName, margin, 70);
  doc.text("Nash IA • Intelligent BPM", margin, 88);
  doc.text(formatDateTime(report.generatedAt), margin, 104);
  y = 148;

  addWrapped(report.headline, { size: 16, bold: true, gap: 10 });
  addWrapped(report.summary, { size: 10.5, gap: 10 });

  if (Array.isArray(report.metrics) && report.metrics.length) {
    sectionTitle("Indicadores principais");
    report.metrics.forEach((metric) => {
      addWrapped(`${safe(metric.label)}: ${safe(metric.value)}`, { bold: true, gap: 2 });
      addWrapped(metric.context, { muted: true, indent: 12, gap: 7 });
    });
  }

  if (Array.isArray(report.diagnostics) && report.diagnostics.length) {
    sectionTitle("Diagnóstico aprofundado");
    report.diagnostics.forEach((item, index) => {
      addWrapped(`${index + 1}. ${safe(item.dimension)} — ${safe(item.finding)}`, { bold: true, gap: 3 });
      addWrapped(`Evidência: ${safe(item.evidence)}`, { muted: true, indent: 12, gap: 3 });
      addWrapped(`Impacto: ${safe(item.impact)}`, { indent: 12, gap: 8 });
    });
  }

  if (Array.isArray(report.insights) && report.insights.length) {
    sectionTitle("Insights e ações");
    report.insights.forEach((item, index) => {
      const priority = item.priority === "high" ? "Alta" : item.priority === "medium" ? "Média" : "Baixa";
      addWrapped(`${index + 1}. ${safe(item.title)} [Prioridade ${priority}]`, { bold: true, gap: 3 });
      addWrapped(item.description, { indent: 12, gap: 3 });
      if (item.action) addWrapped(`Ação recomendada: ${safe(item.action)}`, { indent: 12, bold: true, gap: 8 });
    });
  }

  const sections = [
    ["Riscos", report.risks],
    ["Oportunidades", report.opportunities],
    ["Sinais e tendências", report.forecastSignals],
    ["Qualidade e limitações dos dados", report.dataQuality],
    ["Próximos passos", report.nextSteps],
  ];
  sections.forEach(([label, items]) => {
    if (!Array.isArray(items) || !items.length) return;
    sectionTitle(label);
    bulletList(items);
  });

  const total = doc.getNumberOfPages();
  for (let page = 1; page <= total; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, height - 40, width - margin, height - 40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("Relatório gerado pela Nash IA", margin, height - 24);
    doc.text(`${page}/${total}`, width - margin, height - 24, { align: "right" });
  }

  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `${cleanFileName(module || title || "insights-ia") || "insights-ia"}-${stamp}.pdf`;
  doc.save(fileName);
};

export default downloadAIInsightsPdf;
